import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GetTemplateDetails, UpdateTemplate } from 'api/canmail/CANmailAPI';
import { useNotificationProvider } from 'hooks';
import type { EditorContent, EditorType, LoadedTemplate, TemplateDetails } from '../types';

interface UseTemplateActionsOptions {
  /** Freshest content for the active editor — see `useEmailBody.getFinalHtml`. */
  getFinalHtml: () => Promise<EditorContent>;
  /** Which editor is active, so the right columns get written. */
  mode: EditorType;
  /** Text-editor HTML, needed for `template_content_text`. */
  textContent: string;
  /** Load a Drop design into the composer. */
  loadDropDesign: (designJson: string) => void;
  /** Load text content into the composer. */
  loadTextContent: (html: string) => void;
  /** Blocks saving when a Drop design is loaded on too narrow a viewport. */
  hasUneditableDropContent: boolean;
  /** True when the body is empty. */
  isEmpty: (html?: string) => boolean;
}

export interface UseTemplateActionsResult {
  loadedTemplateId: number | null;
  loadedTemplateName: string;
  updating: boolean;
  /** Adopt a template chosen in `LoadTemplateModal`. */
  applyLoadedTemplate: (template: LoadedTemplate, isDropEditor: boolean) => void;
  /** Track a template that was just saved, so "Update Template" targets it. */
  trackSavedTemplate: (saved: { template_id?: number; template_name?: string } | null) => void;
  /**
   * Update the loaded template in place.
   * @returns whether the confirmation modal should close — it stays open on a
   *          recoverable failure so the user can retry without losing their place.
   */
  updateTemplate: () => Promise<boolean>;
  clearTracking: () => void;
}

/**
 * Owns template tracking (which template is loaded) and the update-in-place action,
 * plus the `?templateId=` deep link from the Template Manager.
 */
export function useTemplateActions({
  getFinalHtml,
  mode,
  textContent,
  loadDropDesign,
  loadTextContent,
  hasUneditableDropContent,
  isEmpty,
}: UseTemplateActionsOptions): UseTemplateActionsResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const { notifyError, notifySuccess } = useNotificationProvider();

  const [loadedTemplateId, setLoadedTemplateId] = useState<number | null>(null);
  const [loadedTemplateName, setLoadedTemplateName] = useState('');
  const [updating, setUpdating] = useState(false);

  // Deep link: ?templateId= hands a template over from the Template Manager.
  useEffect(() => {
    const templateIdParam = searchParams.get('templateId');
    if (!templateIdParam) return;
    const tid = Number(templateIdParam);
    if (Number.isNaN(tid)) return;

    (async () => {
      try {
        const template: TemplateDetails | null = await GetTemplateDetails(tid);
        if (template) {
          const editorType = template.editor_type || (template.template_content_drop ? 'drop' : 'text');
          if (editorType === 'drop') {
            if (template.template_content_drop) {
              loadDropDesign(template.template_content_drop);
            }
          } else {
            loadTextContent(template.template_content_text || '');
          }
          setLoadedTemplateId(template.template_id);
          setLoadedTemplateName(template.template_name || '');
        }
      } catch (err) {
        console.error('[useTemplateActions] Failed to load template from query param:', err);
        notifyError('Could not load the selected template.');
      } finally {
        // Clear the param so navigation/re-renders don't reload it.
        const next = new URLSearchParams(searchParams);
        next.delete('templateId');
        setSearchParams(next, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const applyLoadedTemplate = useCallback(
    (template: LoadedTemplate, isDropEditor: boolean) => {
      // Switch the composer to the editor the template was authored in, so a "drop"
      // template never loads into the text editor (or vice versa).
      if (template.editorType === 'text') {
        loadTextContent(template.content || '');
      } else if (template.editorType === 'drop') {
        loadDropDesign(template.content);
      } else if (isDropEditor) {
        // Legacy fallback: no editor_type recorded, so use the current mode.
        loadDropDesign(template.content);
      } else {
        loadTextContent(template.content);
      }

      setLoadedTemplateId(template.templateId);
      setLoadedTemplateName(template.templateName);
    },
    [loadDropDesign, loadTextContent]
  );

  const trackSavedTemplate = useCallback((saved: { template_id?: number; template_name?: string } | null) => {
    if (saved?.template_id) {
      setLoadedTemplateId(saved.template_id);
      setLoadedTemplateName(saved.template_name || '');
    }
  }, []);

  const updateTemplate = useCallback(async (): Promise<boolean> => {
    if (!loadedTemplateId) {
      notifyError('No template is currently loaded.');
      return false;
    }
    if (hasUneditableDropContent) {
      // Without this the Drop design would be overwritten by the empty text editor.
      notifyError(
        "This template was built in the Drop editor and can't be updated on a narrow screen. " +
        'Open it on a desktop to edit it.'
      );
      return true;
    }

    setUpdating(true);
    try {
      // Export before validating so unsaved Drop edits count as content.
      const { html: renderedHtml, designJson } = await getFinalHtml();

      if (isEmpty(renderedHtml)) {
        notifyError('Email content is required to update template.');
        return false;
      }

      // Drop editor: ContentText = "", ContentDrop = JSON.
      // Text editor:  ContentText = HTML, ContentDrop = "".
      // rendered_html + editor_type drive the Template Manager preview and load.
      await UpdateTemplate(loadedTemplateId, {
        template_content_text: mode === 'drop' ? '' : textContent,
        template_content_drop: mode === 'drop' ? designJson : '',
        rendered_html: renderedHtml,
        editor_type: mode,
      });

      notifySuccess(`Template '${loadedTemplateName}' updated successfully!`);
      return true;
    } catch (error) {
      console.error('[useTemplateActions] Failed to update template:', error);
      notifyError('Failed to update template. Please try again.');
      return false;
    } finally {
      setUpdating(false);
    }
  }, [
    loadedTemplateId,
    loadedTemplateName,
    hasUneditableDropContent,
    getFinalHtml,
    isEmpty,
    mode,
    textContent,
    notifyError,
    notifySuccess,
  ]);

  const clearTracking = useCallback(() => {
    setLoadedTemplateId(null);
    setLoadedTemplateName('');
  }, []);

  return {
    loadedTemplateId,
    loadedTemplateName,
    updating,
    applyLoadedTemplate,
    trackSavedTemplate,
    updateTemplate,
    clearTracking,
  };
}

export default useTemplateActions;
